/**
 * Single source of truth for NIMAR Landscaping & Masonry Inc.
 * Every component pulls contact details, offers and embed codes from here.
 */

export const business = {
  /** Short display name used in headings and navigation */
  name: "NIMAR Landscaping & Masonry",
  /** Registered legal name — used in schema.org and the footer copyright */
  legalName: "NIMAR Landscaping and Masonry Inc.",
  shortName: "NIMAR",
  owner: "Nicolas Marey",
  tagline: "Landscaping & Masonry Done Right",

  /**
   * BUSINESS phone (call-forwarding line printed on the company logo).
   * The owner's personal line is intentionally NOT published anywhere.
   */
  phone: "(781) 521-9603",
  phoneHref: "tel:+17815219603",
  phoneRaw: "+17815219603",

  email: "nimarlandscapingmasonryinc@gmail.com",
  emailHref: "mailto:nimarlandscapingmasonryinc@gmail.com",

  address: {
    street: "73 Chestnut St",
    city: "Wakefield",
    state: "Massachusetts",
    stateShort: "MA",
    zip: "01880",
    country: "US",
    lat: 42.5057,
    lng: -71.0723,
  },
  get addressLine() {
    return `${this.address.street}, ${this.address.city}, ${this.address.stateShort} ${this.address.zip}`;
  },

  hours: {
    label: "Monday – Saturday",
    time: "7:00 AM – 7:00 PM",
    note: "Sunday: Closed",
    /** schema.org openingHours format */
    schema: ["Mo-Sa 07:00-19:00"],
  },

  serviceRadiusMiles: 30,
  serviceAreaLabel: "Greater Boston & Boston's North Shore",

  social: {
    facebook: "https://www.facebook.com/Nimarservices",
  },

  domain: "https://nimarlandscapingandmasonryinc.com",

  /** Founded/experience figures used in stat strips */
  stats: {
    yearsExperience: "15+",
    projectsCompleted: "750+",
    townsServed: "14",
    responseTime: "24h",
  },
} as const;

/**
 * OFFERS
 * NIMAR is a recurring-service business (weekly / bi-weekly maintenance),
 * so it runs a returning-customer discount alongside a referral bonus.
 */
export const offers = {
  returning: {
    headline: "$25 Off Your Next Service",
    detail:
      "Existing maintenance and masonry customers save $25 on their next scheduled service — no coupon needed, just mention it when you book.",
    short: "$25 off your next service",
  },
  referral: {
    headline: "Refer a Neighbor, You Both Save $25",
    detail:
      "Send a neighbor our way and you each get $25 off once their first project is complete. There is no limit on how many neighbors you can refer.",
    short: "Refer a neighbor — you both save $25",
  },
};

/**
 * EMBEDDED WIDGETS
 * These are rendered exactly as supplied by the provider. Never wrap them in
 * fixed heights, max-heights or overflow containers.
 */
export const embeds = {
  /** LeadConnector inline estimate form — used in the hero AND the popup */
  form: {
    src: "https://link.systemic-digital.net/widget/form/9P7FafMHmnYEEEnir87P",
    id: "9P7FafMHmnYEEEnir87P",
    name: "Website Form",
    /**
     * Height the iframe is first rendered at, before the provider's own
     * resizer takes over.
     *
     * The provider declares 670, but the form actually settles at 708px on
     * wide cards and 728px on narrow ones (measured 360px-1920px). Starting
     * at 670 meant a guaranteed resize on every load; 708 matches the common
     * case so most loads never move.
     */
    height: 708,
    script: "https://link.systemic-digital.net/js/form_embed.js",
  },
  /** Reputation review widget */
  reviews: {
    src: "https://reputationhub.site/reputation/widgets/review_widget/eMvq7WYd7ZWph5jfD78o?widgetId=6a7a0ae4fdd615bbb1d03b5e",
    script: "https://reputationhub.site/reputation/assets/review-widget.js",
  },
  /** LeadConnector chat bubble */
  chat: {
    loader: "https://widgets.leadconnectorhq.com/loader.js",
    resourcesUrl: "https://widgets.leadconnectorhq.com/chat-widget/loader.js",
    widgetId: "6a79c902d79119e6613ccef8",
  },
  /**
   * Gallery embed.
   * Paste the media-hub embed snippet between the backticks to switch the
   * gallery page from its "Coming Soon" state to the live gallery.
   */
  gallery: `<style>
.sg-gallery{font-family:inherit;color:inherit;}
.sg-toggle{display:flex;gap:1rem;justify-content:center;margin-bottom:1.5rem;}
.sg-toggle-btn{padding:0.5rem 1.25rem;border-radius:9999px;border:1px solid hsl(var(--border));background:transparent;cursor:pointer;font-weight:500;color:inherit;}
.sg-toggle-btn.active{background:hsl(var(--primary));color:hsl(var(--primary-foreground));border-color:hsl(var(--primary));}
.sg-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;}
.sg-item{position:relative;aspect-ratio:1;overflow:hidden;border-radius:var(--radius);cursor:pointer;background:hsl(var(--muted));border:1px solid hsl(var(--border));}
.sg-item img,.sg-item video{width:100%;height:100%;object-fit:cover;transition:transform .3s ease,opacity .3s ease;}
.sg-item:hover img,.sg-item:hover video{transform:scale(1.05);}
.sg-item img{opacity:0;}.sg-item img.sg-loaded{opacity:1;}
.sg-pagination{display:flex;gap:.5rem;justify-content:center;margin-top:1.5rem;flex-wrap:wrap;}
.sg-page-btn{padding:.5rem 1rem;border-radius:var(--radius);border:1px solid hsl(var(--border));background:hsl(var(--background));cursor:pointer;font-weight:500;color:inherit;}
.sg-page-btn:disabled{opacity:.5;cursor:not-allowed;}
.sg-page-btn.active{background:hsl(var(--primary));color:hsl(var(--primary-foreground));border-color:hsl(var(--primary));}
.sg-overlay{position:fixed;inset:0;background:rgba(0,0,0,.92);z-index:100;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .35s ease;}
.sg-overlay.active{opacity:1;}
.sg-lb-img,.sg-lb-vid{max-width:90vw;max-height:85vh;object-fit:contain;opacity:0;transition:opacity .3s ease;}
.sg-lb-img.visible,.sg-lb-vid.visible{opacity:1;}
.sg-close{position:absolute;top:1rem;right:1rem;background:none;border:none;color:#fff;font-size:2.5rem;cursor:pointer;z-index:10;line-height:1;}
.sg-nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(255,255,255,.15);border:none;color:#fff;width:48px;height:48px;border-radius:50%;font-size:1.5rem;cursor:pointer;z-index:10;display:flex;align-items:center;justify-content:center;}
.sg-nav.prev{left:1rem;}
.sg-nav.next{right:1rem;}
.sg-counter{position:absolute;bottom:1rem;left:50%;transform:translateX(-50%);color:#fff;font-size:.875rem;opacity:.8;}
@media(max-width:768px){.sg-grid{grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:8px;}.sg-nav{width:40px;height:40px;font-size:18px;}.sg-nav.prev{left:8px;}.sg-nav.next{right:8px;}}
@media(max-width:480px){.sg-grid{grid-template-columns:repeat(2,1fr);gap:6px;}}
</style>
<div class="sg-gallery">
  <div id="sg-toggle" class="sg-toggle">
    <button class="sg-toggle-btn active" data-tab="images">Photos</button>
    <button class="sg-toggle-btn" data-tab="videos">Videos</button>
  </div>
  <div id="sg-grid" class="sg-grid"></div>
  <div id="sg-pagination" class="sg-pagination"></div>
</div>
<script>
(function(){
  var images=["https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366695985-865-WhatsApp%20Image%202026-08-10%20at%208.52.41%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366695758-334-WhatsApp%20Image%202026-08-10%20at%208.52.41%20AM%20(1).jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366695501-414-WhatsApp%20Image%202026-08-10%20at%208.52.55%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366695240-777-WhatsApp%20Image%202026-08-10%20at%208.53.35%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366694978-822-WhatsApp%20Image%202026-08-10%20at%208.54.21%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366694745-953-WhatsApp%20Image%202026-08-10%20at%208.54.21%20AM%20(1).jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366694509-237-WhatsApp%20Image%202026-08-10%20at%208.55.52%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366694220-746-WhatsApp%20Image%202026-08-10%20at%208.56.06%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366693978-54-WhatsApp%20Image%202026-08-10%20at%208.56.29%20AM.jpeg","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/nimar-landscaping-masorny-inc/images/1786366693498-926-WhatsApp%20Image%202026-08-10%20at%208.56.43%20AM.jpeg"];
  var thumbs=["https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366695985-865-WhatsApp%20Image%202026-08-10%20at%208.52.41%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366695758-334-WhatsApp%20Image%202026-08-10%20at%208.52.41%20AM%20(1).jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366695501-414-WhatsApp%20Image%202026-08-10%20at%208.52.55%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366695240-777-WhatsApp%20Image%202026-08-10%20at%208.53.35%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366694978-822-WhatsApp%20Image%202026-08-10%20at%208.54.21%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366694745-953-WhatsApp%20Image%202026-08-10%20at%208.54.21%20AM%20(1).jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366694509-237-WhatsApp%20Image%202026-08-10%20at%208.55.52%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366694220-746-WhatsApp%20Image%202026-08-10%20at%208.56.06%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366693978-54-WhatsApp%20Image%202026-08-10%20at%208.56.29%20AM.jpeg?width=480&height=480&resize=cover&quality=50","https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/nimar-landscaping-masorny-inc/images/1786366693498-926-WhatsApp%20Image%202026-08-10%20at%208.56.43%20AM.jpeg?width=480&height=480&resize=cover&quality=50"];
  var videos=[];
  var perPage=20;
  var tab='images';
  var page=0;
  var grid=document.getElementById('sg-grid'),pag=document.getElementById('sg-pagination'),tog=document.getElementById('sg-toggle');

  function getItems(){return tab==='images'?images:videos;}
  function getThumbs(){return tab==='images'?thumbs:videos;}
  function getTotal(){return Math.ceil(getItems().length/perPage);}
  function isVideo(){return tab==='videos';}

  var videoExts=['mp4','mov','webm','avi','mkv'];
  function isVidUrl(u){var ext=u.split('?')[0].split('.').pop().toLowerCase();return videoExts.indexOf(ext)!==-1;}

  var observer=null;
  if('IntersectionObserver' in window){
    observer=new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if(e.isIntersecting){
          var img=e.target;
          if(img.dataset.src){
            img.src=img.dataset.src;
            img.onload=function(){img.classList.add('sg-loaded');};
            img.onerror=function(){
              if(img.dataset.full&&img.src!==img.dataset.full){img.src=img.dataset.full;img.onload=function(){img.classList.add('sg-loaded');};}
            };
            delete img.dataset.src;
          }
          observer.unobserve(img);
        }
      });
    },{rootMargin:'200px'});
  }

  function render(){
    var items=getItems(),total=getTotal(),th=getThumbs();
    if(page>=total)page=Math.max(0,total-1);
    var start=page*perPage,slice=items.slice(start,start+perPage),thSlice=th.slice(start,start+perPage);
    if(isVideo()){
      grid.innerHTML=slice.map(function(u,i){return '<div class="sg-item" data-idx="'+(start+i)+'"><video src="'+u+'#t=0.1" muted preload="metadata"></video><div class="sg-play"></div></div>';}).join('');
    }else{
      grid.innerHTML=slice.map(function(u,i){return '<div class="sg-item" data-idx="'+(start+i)+'"><img data-src="'+thSlice[i]+'" data-full="'+u+'" alt="" decoding="async"/></div>';}).join('');
      if(observer){
        grid.querySelectorAll('img[data-src]').forEach(function(img){observer.observe(img);});
      }else{
        grid.querySelectorAll('img[data-src]').forEach(function(img){
          img.src=img.dataset.src;img.onload=function(){img.classList.add('sg-loaded');};
          img.onerror=function(){if(img.dataset.full){img.src=img.dataset.full;img.onload=function(){img.classList.add('sg-loaded');};}};
        });
      }
    }
    if(total>1){
      var btns='<button class="sg-page-btn" id="sg-prev">&#8249; Prev</button>';
      for(var p=0;p<total;p++) btns+='<button class="sg-page-btn'+(p===page?' active':'')+'" data-p="'+p+'">'+(p+1)+'</button>';
      btns+='<button class="sg-page-btn" id="sg-next">Next &#8250;</button>';
      pag.innerHTML=btns;pag.style.display='flex';
      document.getElementById('sg-prev').disabled=page===0;
      document.getElementById('sg-next').disabled=page===total-1;
      pag.querySelectorAll('[data-p]').forEach(function(b){b.onclick=function(){page=+b.dataset.p;render();};});
      document.getElementById('sg-prev').onclick=function(){if(page>0){page--;render();}};
      document.getElementById('sg-next').onclick=function(){if(page<total-1){page++;render();}};
    }else{pag.innerHTML='';pag.style.display='none';}
    grid.querySelectorAll('.sg-item').forEach(function(el){el.onclick=function(){openLightbox(+el.dataset.idx);};});
  }

  if(tog){
    tog.querySelectorAll('.sg-toggle-btn').forEach(function(b){
      b.onclick=function(){
        tab=b.dataset.tab;page=0;
        tog.querySelectorAll('.sg-toggle-btn').forEach(function(x){x.classList.remove('active');});
        b.classList.add('active');
        render();
      };
    });
  }

  var overlay,viewIdx=0,touchStartX=0;

  function preloadAdj(){
    var items=getItems();
    var prev=(viewIdx-1+items.length)%items.length;
    var next=(viewIdx+1)%items.length;
    if(!isVideo()){new Image().src=items[prev];new Image().src=items[next];}
  }
function showMedia(){
    var items=getItems();
    var old=overlay.querySelector('.sg-lb-img,.sg-lb-vid');
    if(old){old.classList.remove('visible');old.remove();}
    var el;
    if(isVideo()){
      el=document.createElement('video');el.className='sg-lb-vid';el.src=items[viewIdx];el.controls=true;el.autoplay=true;el.playsInline=true;
      el.onloadeddata=function(){el.classList.add('visible');};
    }else{
      el=document.createElement('img');el.className='sg-lb-img';el.src=items[viewIdx];
      el.onload=function(){el.classList.add('visible');};
    }
    var nav=overlay.querySelector('.next');
    overlay.insertBefore(el,nav);
    overlay.querySelector('.sg-counter').textContent=(viewIdx+1)+' / '+items.length;
    preloadAdj();
  }

  function openLightbox(i){
    var items=getItems();
    viewIdx=i;
    overlay=document.createElement('div');overlay.className='sg-overlay';
    var mediaEl;
    if(isVideo()){
      mediaEl='<video class="sg-lb-vid visible" src="'+items[i]+'" controls autoplay playsinline></video>';
    }else{
      mediaEl='<img class="sg-lb-img visible" src="'+items[i]+'" />';
    }
    overlay.innerHTML='<button class="sg-close">&times;</button><button class="sg-nav prev">&#8249;</button>'+mediaEl+'<button class="sg-nav next">&#8250;</button><div class="sg-counter">'+(i+1)+' / '+items.length+'</div>';
    document.body.appendChild(overlay);
    setTimeout(function(){overlay.classList.add('active');},20);
    preloadAdj();
    overlay.querySelector('.sg-close').onclick=closeLb;
    overlay.onclick=function(e){if(e.target===overlay)closeLb();};
    overlay.querySelector('.prev').onclick=function(e){e.stopPropagation();viewIdx=(viewIdx-1+items.length)%items.length;showMedia();};
    overlay.querySelector('.next').onclick=function(e){e.stopPropagation();viewIdx=(viewIdx+1)%items.length;showMedia();};
    overlay.addEventListener('touchstart',function(e){touchStartX=e.changedTouches[0].screenX;},{passive:true});
    overlay.addEventListener('touchend',function(e){var dx=e.changedTouches[0].screenX-touchStartX;if(Math.abs(dx)>50){if(dx<0){viewIdx=(viewIdx+1)%items.length;}else{viewIdx=(viewIdx-1+items.length)%items.length;}showMedia();}},{passive:true});
    document.addEventListener('keydown',onKey);
  }

  function closeLb(){
    var vid=overlay.querySelector('video');if(vid)vid.pause();
    overlay.classList.remove('active');setTimeout(function(){overlay.remove();},350);document.removeEventListener('keydown',onKey);
  }
  function onKey(e){var items=getItems();if(e.key==='Escape')closeLb();if(e.key==='ArrowLeft'){viewIdx=(viewIdx-1+items.length)%items.length;showMedia();}if(e.key==='ArrowRight'){viewIdx=(viewIdx+1)%items.length;showMedia();}}

  render();
})();
</script>`,
};
